/**
 * Instagram publish/schedule guards: image posts need an image;
 * Reels and video (or video-only carousel) need a video URL or VIDEO slide.
 */

function trimmedUrl(s?: string | null): boolean {
  return Boolean(String(s ?? "").trim());
}

function carouselHasImage(
  slides: Array<{ type: string; url?: string }> | undefined
): boolean {
  return Boolean(
    slides?.some(
      (slide) => String(slide.type).toUpperCase() === "IMAGE" && trimmedUrl(slide.url)
    )
  );
}

function carouselHasVideo(
  slides: Array<{ type: string; url?: string }> | undefined
): boolean {
  return Boolean(
    slides?.some(
      (slide) => String(slide.type).toUpperCase() === "VIDEO" && trimmedUrl(slide.url)
    )
  );
}

/** Video satisfies publish when Reels, explicit video/carousel format, or format omitted (UGC / legacy). */
function instagramVideoCountsForPublish(
  platform: string,
  instagramContentFormat: string | undefined
): boolean {
  const p = String(platform ?? "").toUpperCase();
  if (p.includes("REELS")) return true;
  const fmt = instagramContentFormat;
  if (fmt === "video" || fmt === "carousel") return true;
  if (fmt == null || fmt === "") return true;
  return false;
}

/** Canvas draft: image, Reels/video, or video carousel. */
export function instagramDraftHasPublishableMedia(post: {
  platform: string;
  image_url?: string;
  video_url?: string;
  instagram_content_format?: string;
  carousel_slides?: Array<{ type: string; url?: string }>;
}): boolean {
  const imageOk = trimmedUrl(post.image_url) || carouselHasImage(post.carousel_slides);
  if (imageOk) return true;

  const videoOk = trimmedUrl(post.video_url) || carouselHasVideo(post.carousel_slides);
  if (!videoOk) return false;

  return instagramVideoCountsForPublish(post.platform, post.instagram_content_format);
}

/** Review Posts row (DB shape with carousel_media). */
export function instagramReviewPostHasPublishableMedia(post: {
  platform: string;
  image_url?: string;
  video_url?: string;
  instagram_content_format?: string;
  carousel_media?: Array<{ type: string; url?: string }>;
}): boolean {
  const imageOk = trimmedUrl(post.image_url) || carouselHasImage(post.carousel_media);
  if (imageOk) return true;

  const videoOk = trimmedUrl(post.video_url) || carouselHasVideo(post.carousel_media);
  if (!videoOk) return false;

  return instagramVideoCountsForPublish(post.platform, post.instagram_content_format);
}

/**
 * Facebook publishable-media gate. FB carousel is image-only:
 * single image, single video, OR >=2 IMAGE slides. VIDEO slides are not
 * considered carousel-eligible for FB and would be dropped at publish time.
 */
export function facebookDraftHasPublishableMedia(post: {
  image_url?: string;
  video_url?: string;
  content_format?: string;
  instagram_content_format?: string;
  carousel_slides?: Array<{ type: string; url?: string }>;
}): boolean {
  if (trimmedUrl(post.image_url)) return true;
  if (trimmedUrl(post.video_url)) return true;
  const imageSlides = (post.carousel_slides ?? []).filter(
    (slide) => String(slide.type).toUpperCase() === "IMAGE" && trimmedUrl(slide.url)
  );
  return imageSlides.length >= 2;
}

/** Legacy modal flow with platforms[] array. */
export function instagramSocialPostDataHasPublishableMedia(post: {
  platforms?: string[];
  image_url?: string;
  video_url?: string;
  instagram_content_format?: string;
  carousel_media?: Array<{ type: string; url?: string }>;
}): boolean {
  const platforms = post.platforms ?? [];
  const instagram = platforms.some((pl) =>
    String(pl ?? "").toUpperCase().includes("INSTAGRAM")
  );
  if (!instagram) return true;

  const imageOk = trimmedUrl(post.image_url) || carouselHasImage(post.carousel_media);
  if (imageOk) return true;

  const videoOk = trimmedUrl(post.video_url) || carouselHasVideo(post.carousel_media);
  if (!videoOk) return false;

  const igPlatform =
    platforms.find((pl) => String(pl ?? "").toUpperCase().includes("INSTAGRAM")) ?? "";
  return instagramVideoCountsForPublish(igPlatform, post.instagram_content_format);
}
